import { 
  DonationService,
  ItemService,
  ProductService,
  ServiceAPIService,
  MembershipService,
} from "../../../shared/services";
import { inject, observer } from "mobx-react";
import React from "react";
import { NavigateFunction, Params } from "react-router-dom";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import DeleteItemPopup from "./DeleteItemPopup";
import { firstValueFrom } from "rxjs";
import ItemSection from "./ItemSection";
import ItemReOrderSection from "./productDetail/ItemReOrderSection";
import ItemAlowSection from "./productDetail/ItemAlowSection";
import ItemMediaSection from "./ItemMediaSection";
import ItemPricingSection from "./ItemPricingSection";
import ItemAttributeSection from "./ItemAttributeSection";
import ItemInventorySection from "./productDetail/ItemInventorySection";
import ItemVariantsGrid from "./productDetail/ItemVariantsGrid";
import ItemCheckoutGrid from "./productDetail/ItemCheckoutGrid";
import ItemPurchaseLinkGrid from "./productDetail/ItemPurchaseLinkGrid";
import ItemRelatedGrid from "./productDetail/ItemRelatedGrid";
import ProductVariantDetailMain from "./productDetail/ProductVariantDetailMain";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  Loader,
  MadisonBlueButton,
  ModalComponent,
  RedButton,
  ToastService,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  CheckOutStore,
  ItemsStore,
  PurchaseLinkStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { SystemItemTypes } from "../../../shared/enums";
import {
  DescriptionCell,
  ItemDetailLeftPanel,
  ItemDetailMainWrapper,
  ItemDetailRightPanel,
  LabelCell,
  MainOuterCard,
  ProductDetailWrapper,
} from "../../../shared/styles";
import { INavItem } from "../../../shared/interfaces";
import {
  DashboardTopBar,
  ItemContentArea,
  NavPills,
  SplitDropDown,
} from "../../../shared/components";
import {
  InventorySettings,
  ItemDetailPageModel,
  ProductItemModel,
  ProductVariantEditableModel,
  RelatedSectionModel,
} from "../../../shared/models";
import VariantEditPopUp from "./productDetail/variants/VariantEditPopUp";
import { MerchantAccountStore } from "../../merchantAccounts";

interface IProps {
  navigate: NavigateFunction;
  params: Readonly<Params<string>>;
  itemStore: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
}

interface IState {
  activeKey: string;
  showEditPopup: boolean;
  isDeleteAPIInProgress: boolean;
}

const purchaseLinkStore = new PurchaseLinkStore();
const checkoutStore = new CheckOutStore();

@inject("itemStore")
@observer
class ItemDetailPageMain extends React.Component<IProps, IState> {
  public loader: Loader = new Loader(false);

  constructor(props: IProps) {
    super(props);
    this.state = {
      activeKey: "details",
      showEditPopup: false,
      isDeleteAPIInProgress: false,
    };
  }

  updateActiveKey = (key: string) => {
    this.setState({ activeKey: key });
  };

  get itemStore() {
    return this.props.itemStore;
  }

  get itemId() {
    return this.props.params.id;
  }

  get itemType() {
    return this.props.params.type;
  }

  get selectedProduct() {
    return this.props.itemStore.selectedProduct;
  }

  componentDidMount(): void {
    this.fetchDetailPageData(true);
  }

  fetchDetailPageData(refresh: boolean = false): void {
    if (!this.itemId || !this.itemType) {
      return;
    }
    if (refresh) {
      this.fetchData();
      this.getGridData();
    }
    this.setState({ showEditPopup: false });
  }

  private async getGridData(): Promise<void> {
    this.loader.show();
    const response = await firstValueFrom(
      ItemService.getItems(
        AuthService.authData.accountId,
        1,
        99999,
        this.itemStore.searchValue
      )
    );
    if (response?.result) {
      this.itemStore.deserializeGridData(
        response.result || [],
        response.meta,
        true
      );
      this.loader.hide();
    }
  }

  private async fetchData(): Promise<void> {
    if (!this.itemId || !this.itemType) return;
    this.loader.show();
    try {
      let result;
      switch (this.itemType as keyof typeof SystemItemTypes) {
        case SystemItemTypes.Product:
          result = await firstValueFrom(
            ProductService.getProduct(
              AuthService.authData.accountId,
              this.itemId
            )
          );
          if (result?.result) {
            this.itemStore.constructProductItemModel(result.result);
          }
          break;
        case SystemItemTypes.Service:
          result = await firstValueFrom(
            ServiceAPIService.getService(
              AuthService.authData.accountId,
              this.itemId
            )
          );
          if (result?.result) {
            this.itemStore.constructServiceItemModel(result.result);
          }
          break;
        case SystemItemTypes.Membership:
          result = await firstValueFrom(
            MembershipService.getMembership(
              AuthService.authData.accountId,
              this.itemId
            )
          );
          if (result?.result) {
            this.itemStore.constructMembershipItemModel(result.result);
          }
          break;
        case SystemItemTypes.Donation:
          result = await firstValueFrom(
            DonationService.getDonation(
              AuthService.authData.accountId,
              this.itemId
            )
          );
          if (result?.result) {
            this.itemStore.constructDonationItemModel(result.result);
          }
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      this.loader.hide();
    }
  }

  componentDidUpdate(prevProps: IProps) {
    const { params } = this.props;
    if (!params.id || !params.type) return;
    if (
      params.id !== prevProps.params.id ||
      params.type !== prevProps.params.type
    ) {
      this.loader.show();
      this.fetchData();
    }
  }

  componentWillUnmount(): void {
    this.itemStore.setBreadCrumbData();
  }

  row(label: string, rightColumn: string | React.ReactNode): React.ReactNode {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel>{label}</CustomLabel>
        </LabelCell>
        <DescriptionCell>{rightColumn}</DescriptionCell>
      </FlexContainer>
    );
  }

  get navPills(): INavItem[] {
    return [
      {
        key: "details",
        title: "DETAILS",
      },
      {
        key: "variants",
        title: "VARIANTS",
      },
    ];
  }

  get productNavPills() {
    const navPills = this.navPills.filter(
      (x) =>
        this.selectedProduct?.detailSection.hasVariants || x.key !== "variants"
    );
    return (
      <>
        <FlexContainer alignItems="center" padding="0px 0px 30px">
          <CustomLabel color="#2C3E50" fontSize="30px" fontWeight="600">
            Item
          </CustomLabel>
          {navPills.length === 1 ? null : (
            <div className="flex-auto-grow">
              <NavPills
                defaultActiveKey="details"
                pills={navPills}
                activeKey={this.state.activeKey}
                onSelect={(key) => this.updateActiveKey(key)}
              />
            </div>
          )}
        </FlexContainer>
      </>
    );
  }

  updateDonationStatus = (status: boolean) => {
    if (!this.itemId) return;
    DonationService.updateStatus(
      AuthService.authData.accountId,
      this.itemId,
      status
    ).subscribe({
      next: (result) => {
        if (!result.result) {
          ToastService.showError("Failed to update status");
        } else {
          // Handle success
          this.itemStore.updateSelectedDonation(
            new ProductItemModel({
              ...this.itemStore.selectedDonation,
              detailSection: new ItemDetailPageModel({
                ...this.itemStore.selectedDonation?.detailSection,
                isActive: status,
              }),
            })
          );
        }
      },
    });
  };

  updateStatus = (
    status: boolean,
    isProduct: boolean,
    isMembership: boolean,
    isService: boolean
  ) => {
    if (!this.itemId) return;
    if (!this.props.rolesAndPermissionStore.hasManagePermission("Edit")) return;

    if (isProduct) {
      if (!this.selectedProduct) return;

      ProductService.updateStatus(
        AuthService.authData.accountId,
        this.itemId,
        status
      ).subscribe({
        next: (result) => {
          if (!result.result) {
            ToastService.showError("Failed to update status");
          } else {
            // Handle success
            this.itemStore.updateSelectedProduct(
              new ProductItemModel({
                ...this.selectedProduct,
                detailSection: new ItemDetailPageModel({
                  ...this.selectedProduct?.detailSection,
                  isActive: status,
                }),
              })
            );
          }
        },
      });
    }

    if (isMembership) {
      MembershipService.updateStatus(
        AuthService.authData.accountId,
        this.itemId,
        status
      ).subscribe({
        next: (result) => {
          if (!result.result) {
            ToastService.showError("Failed to update status");
          } else {
            // Handle success
            this.itemStore.updateSelectedMembership(
              new ProductItemModel({
                ...this.itemStore.selectedMembership,
                detailSection: new ItemDetailPageModel({
                  ...this.itemStore.selectedMembership?.detailSection,
                  isActive: status,
                }),
              })
            );
          }
        },
      });
    }

    if (isService) {
      ServiceAPIService.updateStatus(
        AuthService.authData.accountId,
        this.itemId,
        status
      ).subscribe({
        next: (result) => {
          if (!result.result) {
            ToastService.showError("Failed to update status");
          } else {
            // Handle success
            this.itemStore.updateSelectedService(
              new ProductItemModel({
                ...this.itemStore.selectedService,
                detailSection: new ItemDetailPageModel({
                  ...this.itemStore.selectedService?.detailSection,
                  isActive: status,
                }),
              })
            );
          }
        },
      });
    }
  };

  onEditVariant(index: number) {
    const newProduct = new ProductItemModel({
      ...this.itemStore.selectedProduct,
      isEditVariant: true,
      selectedVariantIndex: index,
      selectedVariant: this.itemStore.selectedProduct?.variants?.options[index],
    });
    newProduct.editableVariant = ProductVariantEditableModel.init(newProduct);
    this.setState({ showEditPopup: true });
    this.itemStore.updateSelectedProduct(newProduct);
  }

  get productDetails() {
    if (!this.selectedProduct) return null;
    switch (this.state.activeKey) {
      case "details":
        return (
          <>
            <MainOuterCard>
              {this.productNavPills}
              {this.state.showEditPopup && (
                <VariantEditPopUp
                  itemStore={this.itemStore}
                  productName={this.selectedProduct.detailSection.name}
                  onCloseVariantEditPopup={(refresh) =>
                    this.fetchDetailPageData(refresh)
                  }
                  rolesAndPermissionStore={this.props.rolesAndPermissionStore}
                />
              )}
              <FlexContainer padding="40px 0px 0px" className="bg-white">
                <ItemDetailLeftPanel>
                  <ItemSection
                    item={this.selectedProduct.detailSection}
                    type={this.itemType?.toString() || ""}
                    onStatusChange={(status) =>
                      this.updateStatus(status, true, false, false)
                    }
                  />
                  <ItemPricingSection
                    pricing={this.selectedProduct.pricingInputs}
                  />
                  <ItemAttributeSection
                    attributes={this.selectedProduct.selectedAttributes}
                  />{" "}
                </ItemDetailLeftPanel>
                <ItemDetailRightPanel>
                  <ItemMediaSection
                    medias={this.selectedProduct.detailSection.medias || []}
                  />
                  <ItemAlowSection
                    tax={this.selectedProduct.detailSection.tax}
                    discount={this.selectedProduct.detailSection.discount}
                    reviews={this.selectedProduct.detailSection.review}
                    returnDays={this.selectedProduct.detailSection.returnDays}
                  />
                  <ItemReOrderSection
                    reOrderSettings={this.selectedProduct.reOrderSettings}
                    allowReOrder={this.selectedProduct.allowReOrder}
                  />
                  <ItemInventorySection
                    rolesAndPermissionStore={this.props.rolesAndPermissionStore}
                    inventorySettings={this.selectedProduct.inventorySettings}
                    isTrackInventory={this.selectedProduct.isTrackInventory}
                    hasVariants={this.selectedProduct.detailSection.hasVariants}
                    productId={this.selectedProduct.detailSection.id}
                    onUpdate={(inventorySettings) =>
                      this.itemStore.updateSelectedProduct(
                        new ProductItemModel({
                          ...this.itemStore.selectedProduct,
                          inventorySettings: new InventorySettings(
                            inventorySettings
                          ),
                        })
                      )
                    }
                  />
                </ItemDetailRightPanel>
              </FlexContainer>
            </MainOuterCard>
            {this.selectedProduct.variants && (
              <ItemVariantsGrid
                attributes={this.selectedProduct.variants}
                itemStore={this.itemStore}
                onClickEditVariant={(index) => this.onEditVariant(index)}
                standardPricing={
                  this.selectedProduct?.pricingInputs?.termSettings
                    ?.standardPricing
                }
                rolesAndPermissionStore={this.props.rolesAndPermissionStore}
              />
            )}

            <ItemCheckoutGrid
              itemId={this.itemId || ""}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
              checkoutStore={checkoutStore}
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
            />
            <ItemPurchaseLinkGrid
              itemId={this.itemId || ""}
              itemDetails={this.selectedProduct.detailSection}
              relatedSectionData={this.selectedProduct.relatedSectionData}
              isTrackInventory={this.selectedProduct.isTrackInventory}
              purchaseLinkStore={purchaseLinkStore}
               countriesWithState={this.itemStore.countryStore.countriesWithState}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
              variants={
                this.selectedProduct.variants
                  ? this.selectedProduct.variants
                  : undefined
              }
              pricingInputs={[this.selectedProduct.pricingInputs]}
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
            />
            <ItemRelatedGrid
              includedItems={
                this.selectedProduct.relatedSectionData?.includedItems
                  ? [this.selectedProduct.relatedSectionData?.includedItems]
                  : []
              }
              requiredItems={
                this.selectedProduct.relatedSectionData?.requiredItems
              }
              recommendedItems={
                this.selectedProduct.relatedSectionData?.recommendedItems
              }
            />
          </>
        );
      case "variants":
        return (
          <>
            <MainOuterCard>
              {this.productNavPills}

              <FlexContainer padding="40px 0px 0px" className="bg-white">
                {this.selectedProduct.variants && (
                  <ProductVariantDetailMain
                    productName={this.selectedProduct.detailSection.name}
                    onCloseVariantEditPopup={(refresh) =>
                      this.fetchDetailPageData(refresh)
                    }
                    rolesAndPermissionStore={this.props.rolesAndPermissionStore}
                  />
                )}
              </FlexContainer>
            </MainOuterCard>
          </>
        );
      default:
        return <MainOuterCard> {this.productNavPills}</MainOuterCard>;
    }
  }

  get serviceDetails() {
    if (!this.itemStore.selectedService) return null;
    switch (this.state.activeKey) {
      case "details":
        return (
          <>
            <MainOuterCard>
              {this.navPillsWithoutVariants}
              <ItemSection
                onStatusChange={(status) =>
                  this.updateStatus(status, false, false, true)
                }
                item={this.itemStore.selectedService.detailSection}
                type={this.itemType?.toString() || ""}
              />
              <ItemPricingSection
                pricing={this.itemStore.selectedService.pricingInputs}
              />
            </MainOuterCard>
            <ItemPurchaseLinkGrid
              itemId={this.itemId || ""}
              itemDetails={this.itemStore.selectedService.detailSection}
              pricingInputs={this.itemStore.selectedService.prices}
               countriesWithState={this.itemStore.countryStore.countriesWithState}
              purchaseLinkStore={purchaseLinkStore}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
              relatedSectionData={
                this.itemStore.selectedService.relatedSectionData
              }
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
            />
            <ItemCheckoutGrid
              itemId={this.itemId || ""}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
              checkoutStore={checkoutStore}
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
            />
          </>
        );
      default:
        return <MainOuterCard>{this.navPillsWithoutVariants}</MainOuterCard>;
    }
  }

  get membershipDetails() {
    if (!this.itemStore.selectedMembership) return null;
    switch (this.state.activeKey) {
      case "details":
        return (
          <>
            <MainOuterCard>
              {this.navPillsWithoutVariants}
              <ItemSection
                onStatusChange={(status) =>
                  this.updateStatus(status, false, true, false)
                }
                item={this.itemStore.selectedMembership.detailSection}
                type={this.itemType?.toString() || ""}
              />
              <ItemPricingSection
                pricing={this.itemStore.selectedMembership.pricingInputs}
              />
            </MainOuterCard>
            <ItemCheckoutGrid
              itemId={this.itemId || ""}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
              checkoutStore={checkoutStore}
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
            />
            <ItemPurchaseLinkGrid
              itemId={this.itemId || ""}
              itemDetails={this.itemStore.selectedMembership.detailSection}
              pricingInputs={this.itemStore.selectedMembership.prices}
              purchaseLinkStore={purchaseLinkStore}
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
               countriesWithState={this.itemStore.countryStore.countriesWithState}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
              relatedSectionData={
                this.itemStore.selectedMembership.relatedSectionData
              }
            />
            <ItemRelatedGrid
              includedItems={
                this.itemStore.selectedMembership.relatedSectionData
                  ?.includedItems
                  ? [
                      this.itemStore.selectedMembership.relatedSectionData
                        ?.includedItems,
                    ]
                  : []
              }
              requiredItems={
                this.itemStore.selectedMembership.relatedSectionData
                  ?.requiredItems
              }
              recommendedItems={
                this.itemStore.selectedMembership.relatedSectionData
                  ?.recommendedItems
              }
            />
          </>
        );

      default:
        return <MainOuterCard>{this.navPillsWithoutVariants}</MainOuterCard>;
    }
  }

  get donationDetails() {
    if (!this.itemStore.selectedDonation) return null;
    switch (this.state.activeKey) {
      case "details":
        return (
          <>
            <MainOuterCard>
              {this.navPillsWithoutVariants}
              <ItemSection
                onStatusChange={(status) => this.updateDonationStatus(status)}
                item={this.itemStore.selectedDonation.detailSection}
                type={this.itemType?.toString() || ""}
              />
            </MainOuterCard>
            <ItemCheckoutGrid
              itemId={this.itemId || ""}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
              checkoutStore={checkoutStore}
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
            />
            <ItemPurchaseLinkGrid
              itemId={this.itemId || ""}
              itemDetails={this.itemStore.selectedDonation.detailSection}
              purchaseLinkStore={purchaseLinkStore}
              itemType={(this.itemType as keyof typeof SystemItemTypes) || ""}
               countriesWithState={this.itemStore.countryStore.countriesWithState}
              rolesAndPermissionStore={this.props.rolesAndPermissionStore}
              relatedSectionData={
                new RelatedSectionModel({
                  includedItems: this.itemStore.selectedDonation.includedItems
                    ? this.itemStore.selectedDonation.includedItems[0]
                    : undefined,
                  recommendedItems:
                    this.itemStore.selectedDonation.recommendedItems,
                })
              }
            />
            <ItemRelatedGrid
              includedItems={this.itemStore.selectedDonation.includedItems}
              recommendedItems={
                this.itemStore.selectedDonation.recommendedItems
              }
            />
          </>
        );
      default:
        return <MainOuterCard>{this.navPillsWithoutVariants}</MainOuterCard>;
    }
  }

  get body() {
    switch (this.itemType as keyof typeof SystemItemTypes) {
      case SystemItemTypes.Product:
        if (!this.selectedProduct) return null;
        return <>{this.productDetails}</>;
      case SystemItemTypes.Membership:
        if (!this.itemStore.selectedMembership) return null;
        return <>{this.membershipDetails}</>;
      case SystemItemTypes.Service:
        if (!this.itemStore.selectedService) return null;
        return <>{this.serviceDetails}</>;
      case SystemItemTypes.Donation:
        if (!this.itemStore.selectedDonation) return null;
        return <>{this.donationDetails}</>;
      default:
        return null;
    }
  }

  get navPillsWithoutVariants() {
    const navPills = this.navPills.filter((x) => x.key !== "variants");
    return (
      <FlexContainer alignItems="center" padding="0px 0px 30px">
        <CustomLabel color="#2C3E50" fontSize="30px" fontWeight="600">
          Item
        </CustomLabel>
        {!(navPills.length === 1) ? (
          <div className="flex-auto-grow">
            <NavPills
              defaultActiveKey="details"
              pills={navPills}
              onSelect={(key) => this.updateActiveKey(key)}
            />
          </div>
        ) : null}
      </FlexContainer>
    );
  }

  edit(id: string, type: keyof typeof SystemItemTypes) {
    switch (type) {
      case "Product":
        this.props.navigate(`/app/product/detail/${id}`);
        break;
      case "Membership":
        this.props.navigate(`/app/membership/detail/${id}`);
        break;
      case "Service":
        this.props.navigate(`/app/service/detail/${id}`);
        break;
      case "Donation":
        this.props.navigate(`/app/donation/detail/${id}`);
        break;
      default:
        break;
    }
  }

  delete(id: string, type: keyof typeof SystemItemTypes) {
    this.itemStore.setSelectedItemDetails(type, id);
    this.setState({ isDeleteAPIInProgress: true });
    this.itemStore.toggleShowProductDeletePopup();
    switch (type) {
      case SystemItemTypes.Product:
        ProductService.getProductReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            ProductService.getProduct(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                this.setState({ isDeleteAPIInProgress: false });
                if (!result.result) {
                  console.log("error");
                } else {
                  this.itemStore.constructProductItemModel(result.result);
                }
              },
            });
          },
        });
        break;
      case SystemItemTypes.Service:
        ServiceAPIService.getServiceReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            ServiceAPIService.getService(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                this.setState({ isDeleteAPIInProgress: false });
                if (!result.result) {
                  console.log("error");
                } else {
                  this.itemStore.constructServiceItemModel(result.result);
                }
              },
            });
          },
        });
        break;
      case SystemItemTypes.Membership:
        MembershipService.getMembershipReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            MembershipService.getMembership(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                this.setState({ isDeleteAPIInProgress: false });
                if (!result.result) {
                  console.log("error");
                } else {
                  this.itemStore.constructMembershipItemModel(result.result);
                }
              },
            });
          },
        });
        break;
      case SystemItemTypes.Donation:
        DonationService.geDonationReferenceInPurchases(
          AuthService.authData.accountId,
          id
        ).subscribe({
          next: (result) => {
            this.itemStore.setPurchasedItemDetails(result.result);
            DonationService.getDonation(
              AuthService.authData.accountId,
              id
            ).subscribe({
              next: (result) => {
                if (!result.result) {
                  console.log("error");
                } else {
                  this.setState({ isDeleteAPIInProgress: false });
                  this.itemStore.constructDonationItemModel(result.result);
                }
              },
            });
          },
        });
        break;

      default:
        break;
    }
  }

  get actionDropdown() {
    return (
      <SplitDropDown
        label="Edit"
        onClick={() => {
          if (!this.itemId || !this.itemType) return;
          if (!this.props.rolesAndPermissionStore.hasManagePermission("Edit"))
            return;
          this.edit(this.itemId, this.itemType as keyof typeof SystemItemTypes);
        }}
        className="edit-dropdown-item"
        options={[
          {
            icon: EditIcon,
            label: "Edit",
            value: "edit",

            onClick: () => {
              if (!this.itemId || !this.itemType) return;
              if (
                !this.props.rolesAndPermissionStore.hasManagePermission("Edit")
              )
                return;
              this.edit(
                this.itemId,
                this.itemType as keyof typeof SystemItemTypes
              );
            },
          },
          {
            icon: DeleteIcon,
            label: "Delete",
            value: "delete",

            onClick: () => {
              if (!this.itemId || !this.itemType) return;
              if (
                !this.props.rolesAndPermissionStore.hasManagePermission(
                  "Delete"
                )
              )
                return;
              this.delete(
                this.itemId,
                this.itemType as keyof typeof SystemItemTypes
              );
            },
          },
        ]}
      />
    );
  }

  renderDeletePopUpContent = () => {
    const {
      selectedProduct,
      selectedService,
      selectedMembership,
      selectedDonation,
    } = this.itemStore;
    switch (this.itemStore.selectedItemDetails?.type) {
      case SystemItemTypes.Product:
        return (
          <DeleteItemPopup
            selectedItem={selectedProduct}
            itemType={SystemItemTypes.Product}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      case SystemItemTypes.Service:
        return (
          <DeleteItemPopup
            selectedItem={selectedService}
            itemType={SystemItemTypes.Service}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      case SystemItemTypes.Membership:
        return (
          <DeleteItemPopup
            selectedItem={selectedMembership}
            itemType={SystemItemTypes.Membership}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      case SystemItemTypes.Donation:
        return (
          <DeleteItemPopup
            selectedItem={selectedDonation}
            itemType={SystemItemTypes.Donation}
            purchasedItemDetails={this.itemStore.purchasedItemDetails}
          />
        );
      default:
        return null;
    }
  };

  onClickVoid() {
    const { selectedItemDetails } = this.itemStore;
    if (!selectedItemDetails) return;

    const serviceMap = {
      Product: ProductService,
      Service: ServiceAPIService,
      Membership: MembershipService,
      Donation: DonationService,
    };

    const service = serviceMap[selectedItemDetails.type];
    if (service) {
      service
        .deleteItem(AuthService.authData.accountId, selectedItemDetails.id)
        .subscribe({
          next: (result) => {
            if (result.result) {
              this.itemStore.toggleShowProductDeletePopup();
              this.props.navigate(`/app/items`);
              ToastService.showSuccess(
                `${selectedItemDetails.type} deleted successfully`
              );
            } else {
              console.log("Error deleting item");
            }
          },
        });
    }
  }

  handleNavigation = (direction: "back" | "next") => {
    const newItem =
      direction === "next" ? this.getNextItemId() : this.getPreviousItemId();
    if (newItem) {
      this.props.navigate(
        `/app/items/detail/${newItem.id}/${newItem.type}/${newItem.name}`
      );
    } else {
      ToastService.showError("No more items in this direction.");
    }
  };

  getNextItemId = () => {
    const currentIndex = this.itemStore.itemsGridData.findIndex(
      (item) => item.id === this.itemId
    );
    if (
      currentIndex !== -1 &&
      currentIndex < this.props.itemStore.itemsGridData.length - 1
    ) {
      return this.props.itemStore.itemsGridData[currentIndex + 1];
    }
    return null;
  };

  getPreviousItemId = () => {
    const currentIndex = this.itemStore.itemsGridData.findIndex(
      (item) => item.id === this.itemId
    );
    if (currentIndex > 0) {
      return this.props.itemStore.itemsGridData[currentIndex - 1];
    }
    return null;
  };

  getCurrentItemPosition = () => {
    const currentIndex = this.props.itemStore.itemsGridData.findIndex(
      (item) => item.id === this.itemId
    );
    return currentIndex !== -1 ? currentIndex + 1 : null;
  };

  render(): React.ReactNode {
    const currentPosition = this.getCurrentItemPosition();
    return (
      <>
        <DashboardTopBar
          itemStore={this.itemStore}
          merchantAccountStore={this.props.merchantAccountStore}
          rolesAndPermissionStore={this.props.rolesAndPermissionStore}
          rightContent={
            <>
              {this.props.itemStore.itemsGridData.length > 0 &&
                !this.loader.showLoader && (
                  <div>
                    {" "}
                    <FlexContainer
                      alignItems="center"
                      spacing={10}
                      justifyContent="flex-end"
                      margin="0px 20px 0px"
                      width="auto"
                    >
                      <MadisonBlueButton
                        minWidth={""}
                        onClick={() => this.handleNavigation("back")}
                        disabled={!this.getPreviousItemId()}
                        lineHeight="inherit"
                        padding="0px 15px"
                      >
                        Back
                      </MadisonBlueButton>
                      <CustomLabel fontSize={"20px"}>
                        {currentPosition
                          ? `${currentPosition} of ${this.props.itemStore.itemGridPagination.totalItemCount < 1 ? this.props.itemStore.itemsGridData.length : this.props.itemStore.itemGridPagination.totalItemCount}`
                          : `0 of ${this.props.itemStore.itemGridPagination.totalItemCount}`}
                      </CustomLabel>
                      <MadisonBlueButton
                        minWidth={""}
                        onClick={() => this.handleNavigation("next")}
                        disabled={!this.getNextItemId()}
                        lineHeight="inherit"
                        padding="0px 15px"
                      >
                        Next
                      </MadisonBlueButton>
                    </FlexContainer>
                  </div>
                )}
              {this.actionDropdown}
            </>
          }
        />
        <ItemContentArea
          itemStore={this.itemStore}
          node={
            <ItemDetailMainWrapper>
              <ProductDetailWrapper>{this.body}</ProductDetailWrapper>
            </ItemDetailMainWrapper>
          }
          showLoader={this.loader.showLoader}
          showMerchantAccountNotificationBar={this.props.merchantAccountStore.hasNotificationBar()}
        />
        {this.itemStore.showProductDeletePopUp && (
          <ModalComponent
            title="Delete Item"
            titleFontSize="22px"
            bodyPadding="45px 50px"
            showFooter={true}
            minHeight="240px"
            wrapperWidth="600px"
            bodyBackgroundColor="#fefefe"
            footerBackground="rgba(255, 0, 0, 0.1)"
            footerHeight="auto"
            redHeader={true}
            blueHeader={false}
            onClose={() => this.itemStore.toggleShowProductDeletePopup()}
            footerContent={
              <FlexContainer
                alignItems="center"
                justifyContent="center"
                spacing={15}
              >
                <RedButton
                  minWidth="120px"
                  height="40px"
                  onClick={() => this.onClickVoid()}
                >
                  Delete
                </RedButton>
                <RedButton
                  primary
                  minWidth="120px"
                  height="40px"
                  onClick={() => this.itemStore.toggleShowProductDeletePopup()}
                >
                  Do Not Delete
                </RedButton>
              </FlexContainer>
            }
          >
            {this.state.isDeleteAPIInProgress
              ? new Loader(true).loaderComponent
              : this.renderDeletePopUpContent()}
          </ModalComponent>
        )}
      </>
    );
  }
}

export default withRouter(ItemDetailPageMain);
